import { FormattedMessage } from 'react-intl'

import BookmarkIcon from 'app/assets/icons/blog/broken-bookmark.svg'
import Stack, { StackProps } from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import Badge, { BadgeProps } from 'app/features/Blog/components/Badge'
import useBoolean from 'app/hooks/useBoolean'
import { OptionModel } from 'app/hooks/useOptions'
import { useGetBlogArticleTags } from 'app/services/Query/blog/blog.query'
import { convertToBlogOptionModel } from 'app/utils/helpers/blog.helpers'

import TagsCollapse from './components/TagsCollapse'

export interface TagPanelProps {
  tag?: string
  readonly?: boolean
  isMobile?: boolean
  withIcon?: boolean
  sx?: StackProps['sx']
  onTagClick?: BadgeProps['onClick']
}

const TagPanel: RFC<TagPanelProps> = ({
  tag,
  readonly,
  isMobile,
  withIcon = false,
  sx,
  onTagClick
}) => {
  const [open, setOpen] = useBoolean(isMobile)
  const { data = [] } = useGetBlogArticleTags({
    options: {
      enabled: false
    }
  })

  const rawTags = tag ?? data
  const tags = convertToBlogOptionModel(rawTags)

  const hasTags = tags.length > 0
  const hasMoreThanOneTag = tags.length > 1
  const collapsedTags = open ? [] : tags.slice(0, 2)
  const initiallyVisibleTags = isMobile ? collapsedTags : tags

  const renderTagBadge = ({ label, value }: OptionModel) => (
    <Badge
      key={value}
      disabled={!!readonly}
      label={label}
      value={value}
      onClick={onTagClick}
    />
  )

  if (!hasTags) return null

  return (
    <Stack
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 2.25,
        ...sx
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'flex-start',
          position: { xs: 'relative', md: 'static' },
          columnGap: 0.5
        }}
      >
        {withIcon && <BookmarkIcon />}

        <Text variant="body2" sx={{ color: 'text.third' }}>
          <FormattedMessage defaultMessage="Tags" />
        </Text>
      </Stack>

      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexWrap: { md: 'wrap' },
          columnGap: { md: 1 },
          rowGap: { md: 1 },
          width: '100%',
          position: 'relative'
        }}
      >
        {!open && (
          <Stack
            sx={{
              ml: 0,
              position: { xs: 'relative', md: 'static' },
              top: { xs: 4, md: 0 },
              flexDirection: 'row',
              alignItems: { md: 'center' },
              flexWrap: 'wrap',
              columnGap: 1,
              rowGap: 1
            }}
          >
            {initiallyVisibleTags.map(renderTagBadge)}
          </Stack>
        )}

        {hasMoreThanOneTag && (
          <TagsCollapse
            open={open}
            onClick={setOpen.toggle}
            title={<FormattedMessage defaultMessage="Some more" />}
            altTitle={<FormattedMessage defaultMessage="Show less" />}
            sx={{
              display: { ...(readonly && { xs: 'none' }), md: 'none' },
              ml: 'auto',
              maxWidth: !open ? '88px' : '100%',
              width: '100%'
            }}
          >
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                left: 0,
                columnGap: 1,
                rowGap: 1
              }}
            >
              {tags.map(renderTagBadge)}
            </Stack>
          </TagsCollapse>
        )}
      </Stack>
    </Stack>
  )
}

export default TagPanel
